import React, { useState, useEffect } from 'react';
import { apiFetch, getUserFacingErrorMessage } from '../services/apiClient';
import './ScoreAdjustment.css';

/**
 * ScoreAdjustment component for adjusting AI-generated scores.
 * 
 * Allows recruiters to:
 * - View original AI score
 * - Adjust score using slider
 * - Add reasoning and comments
 * - Submit adjustment
 */
const ScoreAdjustment = ({ resumeId, originalScore, dimension = 'overall', onAdjustmentSubmitted }) => {
  // Validate and normalize originalScore
  const normalizedScore = typeof originalScore === 'number' && !isNaN(originalScore) 
    ? Math.max(0, Math.min(100, originalScore)) 
    : 0;

  const [adjustedScore, setAdjustedScore] = useState(normalizedScore);
  const [reasoning, setReasoning] = useState('');
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [adjustmentDelta, setAdjustmentDelta] = useState(0);

  useEffect(() => {
    const normalized = typeof originalScore === 'number' && !isNaN(originalScore) 
      ? Math.max(0, Math.min(100, originalScore)) 
      : 0;
    setAdjustedScore(normalized);
    setAdjustmentDelta(0);
  }, [originalScore, resumeId]);

  useEffect(() => {
    const normalized = typeof originalScore === 'number' && !isNaN(originalScore) 
      ? Math.max(0, Math.min(100, originalScore)) 
      : 0;
    setAdjustmentDelta(adjustedScore - normalized);
  }, [adjustedScore, originalScore]);

  const handleScoreChange = (e) => {
    const newScore = parseFloat(e.target.value);
    setAdjustedScore(newScore);
    setSuccess(false);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate inputs
    if (!resumeId) {
      setError('Resume ID is required');
      return;
    }

    const normalized = typeof originalScore === 'number' && !isNaN(originalScore) 
      ? Math.max(0, Math.min(100, originalScore)) 
      : 0;

    if (adjustedScore === normalized) {
      setError('Please adjust the score before saving');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const response = await apiFetch('/api/feedback/adjust-score', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          resume_id: resumeId,
          original_score: normalized,
          adjusted_score: adjustedScore,
          dimension: dimension,
          reasoning: reasoning || undefined,
          comment: comment || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          getUserFacingErrorMessage(
            response.status,
            data,
            `Failed to adjust score: ${response.status} ${response.statusText}`
          )
        );
      }

      setSuccess(true);
      setError(null);
      
      // Callback if provided
      if (onAdjustmentSubmitted) {
        onAdjustmentSubmitted(data.data);
      }

      // Reset form after 2 seconds
      setTimeout(() => {
        setReasoning('');
        setComment('');
        setSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('Error adjusting score:', err);
      setError(err.message || 'Failed to adjust score. Please try again.');
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const getDeltaColor = () => {
    if (adjustmentDelta > 0) return 'positive';
    if (adjustmentDelta < 0) return 'negative';
    return 'neutral';
  };

  const getDeltaIcon = () => {
    if (adjustmentDelta > 0) return 'G��';
    if (adjustmentDelta < 0) return 'G��';
    return 'G��';
  };

  const normalized = typeof originalScore === 'number' && !isNaN(originalScore) 
    ? Math.max(0, Math.min(100, originalScore)) 
    : 0;

  // Don't render if resumeId is missing or score is invalid
  if (!resumeId || (typeof originalScore !== 'number' || isNaN(originalScore))) {
    return null;
  }

  return (
    <div className="score-adjustment">
      <h3 className="score-adjustment-title">Adjust Score</h3>
      
      <div className="score-adjustment-content">
        <div className="score-display-compact">
          <div className="score-item-compact">
            <label>Original</label>
            <div className="score-value-compact original">{normalized.toFixed(1)}</div>
          </div>
          
          <div className="score-item-compact">
            <label>Adjusted</label>
            <div className={`score-value-compact adjusted ${getDeltaColor()}`}>
              {adjustedScore.toFixed(1)}
              {adjustmentDelta !== 0 && (
                <span className={`delta-compact ${getDeltaColor()}`}>
                  {getDeltaIcon()} {Math.abs(adjustmentDelta).toFixed(1)}
                </span>
              )}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="score-adjustment-form-compact">
          <div className="form-group-compact">
            <input
              type="range"
              id="score-slider"
              min="0"
              max="100"
              step="0.5"
              value={adjustedScore}
              onChange={handleScoreChange}
              className="score-slider-compact"
              disabled={loading}
            />
            <div className="slider-labels-compact">
              <span>0</span>
              <span>{adjustedScore.toFixed(1)}</span>
              <span>100</span>
            </div>
          </div>

          <div className="form-group-compact">
            <textarea
              id="reasoning"
              value={reasoning}
              onChange={(e) => setReasoning(e.target.value)}
              placeholder="Reasoning (optional)..."
              rows="1"
              className="form-control-compact"
              disabled={loading}
            />
          </div>

          {error && (
            <div className="alert-compact alert-error">
              {error}
            </div>
          )}

          {success && (
            <div className="alert-compact alert-success">
              G�� Saved
            </div>
          )}

          <button
            type="submit"
            className="btn-compact btn-primary"
            disabled={loading || adjustmentDelta === 0 || !resumeId}
          >
            {loading ? 'Saving...' : 'Save Adjustment'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ScoreAdjustment;

